import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, Download, ExternalLink, FileText, Loader2 } from 'lucide-react'
import { fetchPolicyDocument, signPolicyDocument } from '../../services/policySignService'
import { sanitizeHtml } from '../../utils/sanitizeHtml'

export default function PolicyDocumentView({ token, email, grant, onExpire }) {
  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [signerName, setSignerName] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [signed, setSigned] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchPolicyDocument(token, email, grant)
      setDoc(data)
      setSigned(Boolean(data?.has_signed_current_version))
    } catch (err) {
      const status = err.response?.status
      if (status === 422) {
        onExpire?.()
        return
      }
      setError(err.response?.data?.message || 'Unable to load the document.')
    } finally {
      setLoading(false)
    }
  }, [token, email, grant, onExpire])

  useEffect(() => {
    load()
  }, [load])

  const submit = async (e) => {
    e.preventDefault()
    if (!accepted) {
      setError('You must confirm the statement before signing.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      await signPolicyDocument(token, {
        email,
        signer_name: signerName.trim() || undefined,
        accepted: true,
      }, grant)
      setSigned(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to record your signature. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex w-full max-w-2xl items-center justify-center rounded-2xl bg-white p-12 text-sm text-gray-500 shadow-xl ring-1 ring-gray-100">
        <Loader2 size={18} className="mr-2 animate-spin" aria-hidden /> Loading document…
      </div>
    )
  }

  if (signed && !submitting) {
    return (
      <div className="w-full max-w-2xl rounded-2xl bg-white p-8 text-center shadow-xl ring-1 ring-gray-100">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <CheckCircle2 size={30} aria-hidden />
        </span>
        <h1 className="mt-4 text-lg font-bold text-gray-900">Thank you — your signature is recorded</h1>
        <p className="mt-2 text-sm text-gray-600">
          You have signed <span className="font-semibold">{doc?.name}</span> (version {doc?.version}).
        </p>
        <p className="mt-1 text-sm text-gray-500">
          A confirmation email detailing what you signed has been sent to <span className="font-semibold text-gray-700">{email}</span>.
        </p>
      </div>
    )
  }

  return (
    <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-100 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-gray-900">{doc?.name}</h1>
          <p className="mt-0.5 text-xs text-gray-500">Version {doc?.version} · reviewing as {email}</p>
        </div>
      </div>
      {doc?.description ? (
        <p className="mt-3 text-sm text-gray-600">{doc.description}</p>
      ) : null}

      {error ? (
        <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {doc?.file?.file_url ? (
        <a
          href={doc.file.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm transition-colors hover:border-rapid-orange/50 hover:bg-orange-50/40"
        >
          <span className="flex items-center gap-2 font-semibold text-gray-800">
            <FileText size={16} className="text-rapid-orange" aria-hidden /> {doc.file.file_name || 'Attached document'}
          </span>
          <span className="flex items-center gap-1 text-xs font-semibold text-rapid-orange">
            <Download size={14} aria-hidden /> Open <ExternalLink size={12} aria-hidden />
          </span>
        </a>
      ) : null}

      <div
        className="policy-prose mt-5 max-h-[50vh] overflow-y-auto rounded-lg border border-gray-100 bg-white px-1 text-sm text-gray-800"
        // Body HTML is sanitised on the backend and again here before render.
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(doc?.body_html) }}
      />

      <form onSubmit={submit} className="mt-6 space-y-4">
        <label className="block">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Your full name (optional)</span>
          <input
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            maxLength={255}
            placeholder="As you would like it recorded"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rapid-orange/30"
          />
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 transition-colors hover:bg-orange-50/40">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-rapid-orange focus:ring-rapid-orange/30"
          />
          <span className="text-sm font-medium text-gray-800">
            {doc?.acceptance_statement || 'I have read, understood, agreed and signed the policy document stated above'}
          </span>
        </label>

        <button
          type="submit"
          disabled={submitting || !accepted}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-rapid-orange px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" aria-hidden /> : null}
          {submitting ? 'Recording signature…' : 'Sign & submit'}
        </button>
      </form>
    </div>
  )
}
